/**
 * Sales Orders API Client
 * Version: 1.0.0
 * Created: 2024-12-07
 */

import apiClient from './client';
import {
    CreateSORequest,
    UpdateSORequest,
    SalesOrderListFilters,
    SalesOrderListResponse,
    CustomerPricing,
    SalesOrder
} from '../types/SalesOrder';

export const salesOrdersAPI = {
    // List Orders
    getOrders: async (filters: SalesOrderListFilters): Promise<SalesOrderListResponse> => {
        const response = await apiClient.get('/sales-orders/list', { params: filters });
        return response.data;
    },

    // Get Single Order
    getOrder: async (id: string): Promise<SalesOrder> => {
        const response = await apiClient.get(`/sales-orders/${id}`);
        return response.data;
    },

    // Create Order
    createOrder: async (data: CreateSORequest): Promise<SalesOrder> => {
        const response = await apiClient.post('/sales-orders/create', data);
        return response.data;
    },

    // Update Order
    updateOrder: async (id: string, data: UpdateSORequest): Promise<SalesOrder> => {
        const response = await apiClient.put(`/sales-orders/${id}/update`, data);
        return response.data;
    },

    // --- Customer Pricing ---

    // Check Price (3-tier logic)
    getItemPrice: async (customerId: number, itemId: number, date?: string): Promise<{ price: number | null, source: string }> => {
        const response = await apiClient.get('/sales-orders/price-check', {
            params: { customer_id: customerId, item_id: itemId, order_date: date }
        });
        return response.data;
    },

    // Get Price History
    getCustomerPricingHistory: async (customerId: number, itemId?: number): Promise<CustomerPricing[]> => {
        const response = await apiClient.get('/sales-orders/customer-pricing/history', {
            params: { customer_id: customerId, item_id: itemId }
        });
        return response.data;
    },

    // Add/Update Customer Price
    setCustomerPrice: async (request: {
        customer_id: number,
        item_id: number,
        price: number,
        effective_from: string,
        effective_to?: string,
        notes?: string
    }): Promise<CustomerPricing> => {
        const response = await apiClient.post('/sales-orders/customer-pricing', request);
        return response.data;
    },

    // Cancel Order
    cancelOrder: async (id: string): Promise<SalesOrder> => {
        const response = await apiClient.post(`/sales-orders/${id}/cancel`);
        return response.data;
    },

    // Bulk Operations
    bulkCancel: async (soIds: number[]): Promise<any> => {
        const response = await apiClient.post('/sales-orders/bulk/cancel', { so_ids: soIds });
        return response.data;
    },

    bulkDelete: async (soIds: number[]): Promise<any> => {
        const response = await apiClient.delete('/sales-orders/bulk/delete', { data: { so_ids: soIds } });
        return response.data;
    },

    bulkUpdateStatus: async (soIds: number[], newStatus: string): Promise<any> => {
        const response = await apiClient.post('/sales-orders/bulk/update-status', {
            so_ids: soIds,
            new_status: newStatus
        });
        return response.data;
    },

    // Sequence Settings
    getSequenceSettings: async (): Promise<any> => {
        const response = await apiClient.get('/sales-orders/sequence-settings');
        return response.data;
    },

    // PDF Export
    downloadSinglePDF: async (soId: number): Promise<Blob> => {
        const response = await apiClient.get(`/sales-orders/${soId}/pdf`, {
            responseType: 'blob'
        });
        return response.data;
    },

    downloadBulkPDF: async (soIds: number[]): Promise<Blob> => {
        const response = await apiClient.post('/sales-orders/bulk/export-pdf', { so_ids: soIds }, {
            responseType: 'blob'
        });
        return response.data;
    },

    // Modification History
    getModificationHistory: async (soId: number): Promise<any> => {
        const response = await apiClient.get(`/sales-orders/${soId}/modification-history`);
        return response.data;
    },
};

export default salesOrdersAPI;
